import { Lock, Eye, EyeOff } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import type { User } from '@supabase/supabase-js';

import { supabase } from '../../../lib/supabase';
import { useStudentOnboardingStore } from '../../../store/studentOnboarding.store';
import { Button } from '../../../components/ui/Button';
import {
  PASSWORD_POLICY_PLACEHOLDER,
  validatePassword,
} from '@rollops/app-shared/utils/passwordPolicy';
import { PasswordRulesList } from '../../../components/auth/PasswordRulesList';

interface PasswordStepProps {
  onNext: () => void;
}

export function PasswordStep({ onNext }: PasswordStepProps) {
  const { setPasswordSet } = useStudentOnboardingStore();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [_needsPassword, setNeedsPassword] = useState(true);

  useEffect(() => {
    const checkPasswordStatus = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        // Check if user has a password set
        // If user was created via magic link/OTP, they might not have a password
        // We can check this by attempting to sign in with a dummy password
        // But a simpler approach: check if user has email_confirmed_at but no password
        // Actually, Supabase doesn't expose this directly, so we'll just show the step
        // and let them set a password if needed
        setNeedsPassword(true);
      }
    };
    checkPasswordStatus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    const passwordCheck = validatePassword(password);
    if (!passwordCheck.ok) {
      setError(passwordCheck.reason);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!user) {
      setError('User not found. Please sign in again.');
      return;
    }

    setSaving(true);

    try {
      // Update user password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password.trim(),
      });

      if (updateError) {
        throw updateError;
      }

      // Password set successfully, mark as complete and proceed to next step
      setPasswordSet(true);
      onNext();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to set password. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Allow skipping if user wants to keep existing password
  // But we'll always show the step to ensure password is set for app login

  return (
    <div className="w-full max-w-lg mx-auto bg-[#221F1F] rounded-2xl p-6 border border-[#504A4A]">
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-full bg-[#9B1421]/20 flex items-center justify-center mx-auto mb-4">
          <Lock size={32} className="text-text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-[#EBFFEE]">Set Your Password</h2>
        <p className="text-[#A9A3A3] mt-2">Create a password to securely access your account</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Password <span className="text-rollops-accent">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              aria-describedby="student-password-rules"
              className="w-full px-4 py-3 pr-12 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              placeholder={PASSWORD_POLICY_PLACEHOLDER}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          <PasswordRulesList id="student-password-rules" password={password} />
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Confirm Password <span className="text-rollops-accent">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-4 py-3 pr-12 rounded-lg bg-[#504A4A] text-[#EBFFEE] placeholder:text-[#A9A3A3] border-none outline-none focus:ring-2 focus:ring-[#9B1421]/50"
              placeholder="Confirm your password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <Button
            type="submit"
            disabled={saving || !password || !confirmPassword}
            variant="red"
            size="lg"
            className="w-full font-semibold"
          >
            {saving ? 'Setting Password...' : 'Set Password & Continue'}
          </Button>
          <button
            type="button"
            onClick={() => {
              // Skip password step if user already has one
              setPasswordSet(true);
              onNext();
            }}
            className="text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors"
          >
            I already have a password, skip this step
          </button>
        </div>
      </form>
    </div>
  );
}

